import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../auth/AuthContext";
import { formatApiError, apiForgotPassword, apiResetPassword } from "../lib/api";
import { ShieldCheck, Loader2 } from "lucide-react";

export default function Login() {
  const { t } = useLang();
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) navigate("/history"); }, [user, navigate]);

  useEffect(() => {
    const rt = searchParams.get("reset");
    if (rt) { setResetToken(rt); setMode("reset"); }
    if (searchParams.get("register")) {
      setMode("register");
      const em = searchParams.get("email");
      if (em) setForm((f) => ({ ...f, email: em }));
    }
  }, [searchParams]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setInfo(null); setLoading(true);
    try {
      if (mode === "login") { await login(form.email, form.password); navigate("/history"); }
      else if (mode === "register") { await register({ email: form.email, password: form.password, name: form.name || undefined }); navigate("/history"); }
      else if (mode === "forgot") {
        await apiForgotPassword(form.email);
        setInfo(t("account.resetEmailSent")); setMode("login");
      } else if (mode === "reset") {
        await apiResetPassword(resetToken, newPassword);
        setInfo(t("account.resetDone")); setMode("login"); setNewPassword(""); setResetToken("");
      }
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail, t("account.error")));
    } finally { setLoading(false); }
  };

  const title = mode === "login" ? t("account.loginTitle") : mode === "register" ? t("account.registerTitle")
    : mode === "forgot" ? t("account.forgotTitle") : t("account.resetTitle");
  const submitLabel = mode === "login" ? t("account.loginCta") : mode === "register" ? t("account.registerCta")
    : mode === "forgot" ? t("account.sendReset") : t("account.resetCta");

  const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-600 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200";

  return (
    <div className="tw-grid-bg flex min-h-screen items-center justify-center px-4 pt-28 pb-16">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#1E3A8A] to-[#0D9488]">
            <ShieldCheck className="h-5 w-5 text-white" strokeWidth={2.5} />
          </div>
          <span className="text-lg font-bold text-slate-900">TrustWall</span>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-7">
          <h1 className="text-2xl font-bold text-slate-900" data-testid="auth-title">{title}</h1>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <input data-testid="auth-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={t("account.name")} className={inputCls} />
            )}
            {(mode === "login" || mode === "register" || mode === "forgot") && (
              <input data-testid="auth-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} placeholder={t("account.email")} className={inputCls} />
            )}
            {(mode === "login" || mode === "register") && (
              <div>
                <input data-testid="auth-password" type="password" required value={form.password} onChange={(e) => set("password", e.target.value)} placeholder={t("account.password")} className={inputCls} />
                {mode === "register" && <p className="mt-1.5 text-xs text-slate-500">{t("account.passwordHint")}</p>}
              </div>
            )}
            {mode === "reset" && (
              <>
                {!resetToken && (
                  <input data-testid="reset-token" required value={resetToken} onChange={(e) => setResetToken(e.target.value)} placeholder={t("account.resetToken")} className={inputCls} />
                )}
                <input data-testid="reset-newpw" type="password" required value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder={t("account.newPassword")} className={inputCls} />
              </>
            )}

            {info && <p data-testid="auth-info" className="break-all text-sm text-emerald-700">{info}</p>}
            {error && <p data-testid="auth-error" className="text-sm text-rose-400">{error}</p>}

            <button data-testid="auth-submit" type="submit" disabled={loading} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#1E3A8A] px-6 py-3.5 text-base font-semibold text-white transition-colors hover:bg-[#1D4ED8] disabled:opacity-50">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : submitLabel}
            </button>
          </form>

          {(mode === "login" || mode === "register") && (
            <button
              data-testid="auth-toggle"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(null); setInfo(null); }}
              className="mt-5 w-full text-center text-sm text-blue-700 hover:text-blue-700"
            >
              {mode === "login" ? t("account.toRegister") : t("account.toLogin")}
            </button>
          )}
          {mode === "login" && (
            <button data-testid="auth-forgot" onClick={() => { setMode("forgot"); setError(null); setInfo(null); }} className="mt-2 w-full text-center text-sm text-slate-500 hover:text-slate-600">
              {t("account.forgot")}
            </button>
          )}
          {(mode === "forgot" || mode === "reset") && (
            <button data-testid="auth-back" onClick={() => { setMode("login"); setError(null); }} className="mt-5 w-full text-center text-sm text-blue-700 hover:text-blue-700">
              {t("account.backToLogin")}
            </button>
          )}
        </div>
        <p className="mt-4 text-center text-xs text-slate-500">{t("account.guestNote")}</p>
      </div>
    </div>
  );
}
